import type { ExamSectionId, HireHealthStep, SectionId, TextSectionId } from './hireHealthSurveyTypes';

export const DONE_STEP_ID = '__done__';

export const SECTION_ORDER: SectionId[] = [
  'general',
  'pneumoconiosis',
  'vibration',
  'hearing',
  'specialized',
  'aftereffects',
  'symptoms',
];

export function sectionTitleKey(sectionId: SectionId): string {
  return `submitDocuments.hireHealthSurvey.sections.${sectionId}`;
}

export function stepQuestionKey(step: HireHealthStep): string {
  return `submitDocuments.hireHealthSurvey.steps.${step}`;
}

export function firstStepForSection(sectionId: SectionId): HireHealthStep {
  switch (sectionId) {
    case 'general':
      return 'general_gate';
    case 'pneumoconiosis':
      return 'pneumoconiosis_gate';
    case 'vibration':
      return 'vibration_gate';
    case 'hearing':
      return 'hearing_gate';
    case 'specialized':
      return 'specialized_gate';
    case 'aftereffects':
      return 'aftereffects_gate';
    case 'symptoms':
      return 'symptoms_gate';
    default:
      return 'general_gate';
  }
}

export function sectionIdForStep(step: HireHealthStep): SectionId | null {
  if (step.startsWith('general_')) return 'general';
  if (step.startsWith('pneumoconiosis_')) return 'pneumoconiosis';
  if (step.startsWith('vibration_')) return 'vibration';
  if (step.startsWith('hearing_')) return 'hearing';
  if (step.startsWith('specialized_')) return 'specialized';
  if (step.startsWith('aftereffects_')) return 'aftereffects';
  if (step.startsWith('symptoms_')) return 'symptoms';
  if (step === 'finalConfirm') return null;
  return null;
}

export function nextStepAfter(step: HireHealthStep, hadYes: boolean): HireHealthStep | 'finishSection' {
  switch (step) {
    case 'general_gate':
      return hadYes ? 'general_date' : 'finishSection';
    case 'general_date':
      return 'general_hospital';
    case 'general_hospital':
      return 'general_fit';
    case 'general_fit':
      return 'finishSection';
    case 'pneumoconiosis_gate':
      return hadYes ? 'pneumoconiosis_date' : 'finishSection';
    case 'pneumoconiosis_date':
      return 'pneumoconiosis_hospital';
    case 'pneumoconiosis_hospital':
      return 'pneumoconiosis_fit';
    case 'pneumoconiosis_fit':
      return 'finishSection';
    case 'vibration_gate':
      return hadYes ? 'vibration_date' : 'finishSection';
    case 'vibration_date':
      return 'vibration_hospital';
    case 'vibration_hospital':
      return 'vibration_fit';
    case 'vibration_fit':
      return 'finishSection';
    case 'hearing_gate':
      return hadYes ? 'hearing_date' : 'finishSection';
    case 'hearing_date':
      return 'hearing_hospital';
    case 'hearing_hospital':
      return 'hearing_fit';
    case 'hearing_fit':
      return 'finishSection';
    case 'specialized_gate':
      return hadYes ? 'specialized_date' : 'finishSection';
    case 'specialized_date':
      return 'specialized_hospital';
    case 'specialized_hospital':
      return 'specialized_fit';
    case 'specialized_fit':
      return 'finishSection';
    case 'aftereffects_gate':
      return hadYes ? 'aftereffects_detail' : 'finishSection';
    case 'aftereffects_detail':
      return 'finishSection';
    case 'symptoms_gate':
      return hadYes ? 'symptoms_detail' : 'finishSection';
    case 'symptoms_detail':
      return 'finishSection';
    default:
      return 'finishSection';
  }
}

export function nextSectionAfter(sectionId: SectionId): SectionId | 'finalConfirm' {
  const idx = SECTION_ORDER.indexOf(sectionId);
  if (idx < 0 || idx >= SECTION_ORDER.length - 1) {
    return 'finalConfirm';
  }
  return SECTION_ORDER[idx + 1];
}

export function stepIndex(step: HireHealthStep): number {
  const all: HireHealthStep[] = [
    'general_gate',
    'general_date',
    'general_hospital',
    'general_fit',
    'pneumoconiosis_gate',
    'pneumoconiosis_date',
    'pneumoconiosis_hospital',
    'pneumoconiosis_fit',
    'vibration_gate',
    'vibration_date',
    'vibration_hospital',
    'vibration_fit',
    'hearing_gate',
    'hearing_date',
    'hearing_hospital',
    'hearing_fit',
    'specialized_gate',
    'specialized_date',
    'specialized_hospital',
    'specialized_fit',
    'aftereffects_gate',
    'aftereffects_detail',
    'symptoms_gate',
    'symptoms_detail',
    'finalConfirm',
  ];
  const i = all.indexOf(step);
  return i >= 0 ? i : 0;
}

export function inferCompletedSectionsFromStep(currentStep: HireHealthStep | null): SectionId[] {
  if (!currentStep || currentStep === 'finalConfirm') {
    return [...SECTION_ORDER];
  }
  const sid = sectionIdForStep(currentStep);
  if (!sid) return [];
  const idx = SECTION_ORDER.indexOf(sid);
  if (idx <= 0) return [];
  return SECTION_ORDER.slice(0, idx);
}
